/**
 * Coach name, photo and profile links on the public article pages. Each part
 * links to `/$username` only while `hasPublicProfile` holds: that page 404s
 * for a hidden coach, so the name and photo stay as plain content and the
 * "View profile" link or button is left out.
 *
 * Aliases: article coach byline, article author avatar, article profile link
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Button } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import { cn } from "~/lib/cn";

export type ArticleCoachLinkData = {
  readonly username: string;
  readonly fullName: string;
  readonly profilePictureUrl: string | null;
  readonly hasPublicProfile: boolean;
};

/** Small round photo and name (with credentials) heading an article card. */
export function ArticleCardByline({
  coach,
  credentials,
}: {
  readonly coach: ArticleCoachLinkData;
  readonly credentials: string;
}) {
  const content = (
    <>
      {coach.profilePictureUrl ? (
        <CDNImage
          src={coach.profilePictureUrl}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover"
          width={40}
          height={40}
          loading="lazy"
        />
      ) : (
        <div
          aria-hidden
          className="h-10 w-10 shrink-0 rounded-full bg-purple-100"
        />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-gray-900 group-hover:text-purple-700">
          {coach.fullName}
        </div>
        {credentials ? (
          <div className="truncate text-xs text-gray-500">{credentials}</div>
        ) : null}
      </div>
    </>
  );

  return (
    <ProfileLinkOrPlain
      coach={coach}
      className="flex items-center gap-3"
      linkClassName="group"
    >
      {content}
    </ProfileLinkOrPlain>
  );
}

/** "View <name>'s profile" link at the foot of an article card. */
export function ArticleCardProfileLink({
  coach,
}: {
  readonly coach: ArticleCoachLinkData;
}) {
  if (!coach.hasPublicProfile) return null;
  return (
    <Link
      to="/$username"
      params={{ username: coach.username }}
      className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900"
    >
      View {coach.fullName}'s profile →
    </Link>
  );
}

/** Large round photo in the "About the author" section of an article page. */
export function ArticleAuthorAvatar({
  coach,
}: {
  readonly coach: ArticleCoachLinkData;
}) {
  return (
    <ProfileLinkOrPlain coach={coach} className="shrink-0">
      {coach.profilePictureUrl ? (
        <img
          src={coach.profilePictureUrl}
          alt=""
          width={128}
          height={128}
          className="h-28 w-28 rounded-full object-cover md:h-32 md:w-32"
          loading="lazy"
        />
      ) : (
        <div className="h-28 w-28 rounded-full bg-gray-200 md:h-32 md:w-32" />
      )}
    </ProfileLinkOrPlain>
  );
}

/** "View <first name>'s profile" button in the "About the author" section. */
export function ArticleAuthorProfileButton({
  coach,
}: {
  readonly coach: ArticleCoachLinkData;
}) {
  if (!coach.hasPublicProfile) return null;
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      <Link to="/$username" params={{ username: coach.username }}>
        <Button variant="primary" size="lg">
          View {coach.fullName.split(" ")[0]}'s profile →
        </Button>
      </Link>
    </div>
  );
}

/** Wraps children in a `/$username` link, or a plain div when the profile is hidden. */
export function ProfileLinkOrPlain({
  coach,
  className,
  linkClassName,
  children,
}: {
  readonly coach: Pick<ArticleCoachLinkData, "username" | "hasPublicProfile">;
  readonly className: string;
  /** Hover styling, added only to the link so plain content does not look clickable. */
  readonly linkClassName?: string;
  readonly children: ReactNode;
}) {
  if (!coach.hasPublicProfile)
    return <div className={className}>{children}</div>;
  return (
    <Link
      to="/$username"
      params={{ username: coach.username }}
      className={cn(className, linkClassName)}
    >
      {children}
    </Link>
  );
}
